import React, { createContext, useCallback, useContext, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'

export type ToastItem = { id: number; title?: string; message: string; variant?: 'info'|'success'|'warning'|'error'; timeout?: number }

type ToastContextValue = { show: (msg: Omit<ToastItem, 'id'>) => void }
const ToastContext = createContext<ToastContextValue | undefined>(undefined)

export function useToast() {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast must be used within ToastProvider')
  return ctx
}

export const ToastProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [items, setItems] = useState<ToastItem[]>([])
  const show = useCallback((msg: Omit<ToastItem, 'id'>) => {
    const id = Date.now() + Math.floor(Math.random()*1000)
    const item: ToastItem = { id, timeout: 4000, variant: 'info', ...msg }
    setItems(prev => [item, ...prev])
    if (item.timeout) setTimeout(() => setItems(prev => prev.filter(i => i.id !== id)), item.timeout)
  }, [])

  const remove = (id: number) => setItems(prev => prev.filter(i => i.id !== id))
  const value = useMemo(() => ({ show }), [show])
  return (
    <ToastContext.Provider value={value}>
      {children}
      {createPortal(
        <div className="fixed z-toast bottom-4 right-4 flex flex-col gap-2" aria-live="polite" aria-atomic="true">
          {items.map(t => (
            <div key={t.id} role="status" className={`rounded shadow-elev-2 p-3 w-80 text-sm bg-white dark:bg-gray-900 border ${t.variant === 'success' ? 'border-green-400' : t.variant === 'error' ? 'border-red-400' : t.variant === 'warning' ? 'border-yellow-400' : 'border-gray-300'}`}>
              {t.title && <div className="font-medium mb-1">{t.title}</div>}
              <div className="flex items-start justify-between gap-2">
                <div>{t.message}</div>
                <button className="text-xs opacity-70 hover:opacity-100" onClick={() => remove(t.id)}>✕</button>
              </div>
            </div>
          ))}
        </div>,
        document.getElementById('portal-root') as HTMLElement
      )}
    </ToastContext.Provider>
  )
}
